import { api } from './client'

/**
 * F-122 (TASK-0376, REQ-294-001): client di `ConfigurationCheckController` (`/api/platform/configuration-check`,
 * solo Admin). Sola lettura: cerca riferimenti rotti, regole incompatibili e flussi senza uscita nelle
 * configurazioni attive o pubblicate. Non blocca nulla.
 */
export type ConfigurationSeverity = 'errore' | 'avviso' | 'info'

export interface ConfigurationIssue {
  area: string
  severity: ConfigurationSeverity
  code: string
  entityType: string
  entityId: string
  entityName: string
  message: string
  link: string
}

export interface ConfigurationCheckResult {
  checkedAt: string
  counts: Record<ConfigurationSeverity, number>
  issues: ConfigurationIssue[]
}

export const CONFIGURATION_AREA_LABELS: Record<string, string> = {
  automations: 'Automazioni',
  'lead-routing': 'Instradamento lead',
  'lead-scoring': 'Punteggio lead',
  'call-scripts': 'Script di chiamata',
  webhooks: 'Webhook',
  'document-templates': 'Modelli documento',
  'dynamic-fields': 'Campi dinamici',
  pipelines: 'Pipeline',
  contracts: 'Pratiche (validazione, priorità, SLA)',
  commissions: 'Provvigioni',
}

export const CONFIGURATION_SEVERITY_LABELS: Record<ConfigurationSeverity, string> = {
  errore: 'Errori',
  avviso: 'Avvisi',
  info: 'Informazioni',
}

export const configurationCheckApi = {
  run: (area?: string) =>
    api<ConfigurationCheckResult>(
      `/api/platform/configuration-check${area ? `?area=${encodeURIComponent(area)}` : ''}`),

  /** Solo i problemi di una configurazione, anche se e' ancora una bozza. */
  forEntity: (type: string, id: number) =>
    api<ConfigurationCheckResult>(
      `/api/platform/configuration-check/entity?type=${encodeURIComponent(type)}&id=${id}`),
}

function errorLines(result: ConfigurationCheckResult): string {
  return result.issues
    .filter(i => i.severity === 'errore')
    .map(i => `- ${i.message}`)
    .join('\n')
}

/**
 * Prima di attivare una configurazione: se la verifica trova errori li elenca e chiede conferma. E' un avviso, non
 * un blocco: se l'Admin conferma si attiva comunque; se la verifica stessa non risponde si attiva senza chiedere.
 */
export async function confirmActivation(entityType: string, id: number, name: string): Promise<boolean> {
  let result: ConfigurationCheckResult
  try {
    result = await configurationCheckApi.forEntity(entityType, id)
  } catch {
    return true
  }
  if (result.counts.errore === 0) return true
  return window.confirm(
    `La verifica della configurazione ha trovato ${result.counts.errore} problemi in "${name}":\n\n` +
    `${errorLines(result)}\n\nAttivarla comunque?`)
}

/** Dopo un salvataggio che lascia la configurazione attiva: segnala gli errori trovati (la modifica e' gia' salvata). */
export async function warnAfterSave(entityType: string, id: number, name: string): Promise<void> {
  try {
    const result = await configurationCheckApi.forEntity(entityType, id)
    if (result.counts.errore > 0)
      window.alert(
        `Salvato, ma la verifica della configurazione ha trovato ${result.counts.errore} problemi in "${name}":\n\n` +
        `${errorLines(result)}`)
  } catch {
    // La verifica e' un aiuto: se non risponde non si disturba l'utente.
  }
}
